import { Link } from "react-router-dom";
import { ArrowLeftRight } from "lucide-react";
import { cn } from "@ridereserv/shared/utils";

export interface LogoProps {
  /** Size variant */
  size?: "sm" | "md" | "lg";
  /** Whether to link to home page */
  asLink?: boolean;
  /**
   * Color tone. "default" inherits the app foreground; "inverse" is for dark
   * surfaces such as the sidebar rail.
   */
  tone?: "default" | "inverse";
  /** Additional CSS classes */
  className?: string;
}

/**
 * RideReserv logo: restrained monochrome bordered mark + wordmark.
 * The mark and wordmark both render in `currentColor`, so the whole logo
 * follows the tone (foreground on light surfaces, rail foreground on ink).
 * @param root0 - Logo props
 * @param root0.size - Size variant (sm, md, lg)
 * @param root0.asLink - Whether to wrap in a Link to home
 * @param root0.tone - Color tone (default, inverse)
 * @param root0.className - Additional CSS classes
 * @returns The logo component
 */
export function Logo({
  size = "md",
  asLink = false,
  tone = "default",
  className,
}: LogoProps) {
  const sizes = {
    sm: {
      mark: "h-[18px] w-[18px]",
      icon: "h-2.5 w-2.5",
      text: "text-sm",
    },
    md: {
      mark: "h-5 w-5",
      icon: "h-3 w-3",
      text: "text-[15px]",
    },
    lg: {
      mark: "h-6 w-6",
      icon: "h-3.5 w-3.5",
      text: "text-lg",
    },
  };

  const content = (
    <div
      className={cn(
        "flex items-center gap-2",
        tone === "inverse" ? "text-sidebar-foreground" : "text-foreground",
        className
      )}
    >
      <span
        className={cn(
          "flex items-center justify-center rounded-[4px] border-[1.5px] border-current",
          sizes[size].mark
        )}
      >
        <ArrowLeftRight className={sizes[size].icon} strokeWidth={2.25} />
      </span>
      <span className={cn("font-semibold tracking-tight", sizes[size].text)}>
        RideReserv
      </span>
    </div>
  );

  if (asLink) {
    return (
      <Link to="/" className="flex min-h-11 items-center pointer-fine:min-h-8">
        {content}
      </Link>
    );
  }

  return content;
}
